// V9.13 · Replicação inteligente de planos financeiros (ano · curso · segmento · turma)
// Regra de ouro: aqui só se CONFIGURA plano. Títulos continuam exclusivos do DNA Pay.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Zap, RefreshCw, CheckCircle2, AlertTriangle, SlidersHorizontal } from 'lucide-react';
import api from '@/lib/api';

const money = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const brDate = (s) => (String(s || '').match(/^\d{4}-\d{2}-\d{2}/)
  ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '—');

const TARGET_MODES = [
  ['classes', 'Turmas deste curso'],
  ['segment_courses', 'Outros cursos do mesmo segmento'],
  ['segment', 'Segmento inteiro (cursos + turmas)'],
  ['manual', 'Selecionar manualmente'],
];

const CONFLICT_MODES = [
  ['keep', 'Manter existente (padrão)'],
  ['values_only', 'Atualizar somente valores'],
  ['replace', 'Substituir configuração completa'],
];

const apiError = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.detail) return d.detail;
  return err?.message || 'Erro inesperado';
};

const StatBox = ({ label, value, tone = 'slate' }) => (
  <div className={`rounded-lg border p-3 text-center bg-${tone}-50`}>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
  </div>
);

export const CourseSyncBadge = ({ course, onSync }) => {
  const [showDiff, setShowDiff] = useState(false);
  if (!course) return null;
  const { classes_total: total, classes_synced: synced } = course;
  const ok = total > 0 && synced === total;
  const diverged = (course.classes || []).filter((c) => c.status !== 'synced');
  return (
    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setShowDiff((v) => !v)}
        className={`w-full text-left text-xs font-semibold rounded-md px-2 py-1 border transition-colors ${
          ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
             : 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'}`}
        data-testid={`course-sync-badge-${course.id}`}
      >
        {total === 0 ? '· nenhuma turma no ano' : ok
          ? `✓ ${synced}/${total} turmas sincronizadas`
          : `⚠ ${synced}/${total} turmas sincronizadas`}
      </button>
      {showDiff && diverged.length > 0 && (
        <div className="rounded-md border bg-white p-2 space-y-1" data-testid={`course-sync-diff-${course.id}`}>
          {diverged.slice(0, 6).map((c) => (
            <div key={c.class_group_id} className="text-[11px]">
              <span className="font-semibold">{c.name}</span>
              <span className="text-muted-foreground">
                {' · '}{c.status === 'missing' ? 'sem plano replicado' : c.status === 'no_base' ? 'curso sem plano-base' : 'divergente'}
              </span>
              {(c.diffs || []).slice(0, 3).map((d, i) => (
                <span key={i} className="ml-1 text-amber-700">
                  [{d.category}{d.origem !== undefined ? `: ${money(d.destino)} → ${money(d.origem)}` : ''}]
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
      {total > 0 && (
        <Button size="sm" variant="outline" className="h-7 w-full text-[11px]"
          onClick={() => onSync(course.id)} data-testid={`btn-sync-course-${course.id}`}>
          <RefreshCw className="w-3 h-3 mr-1" /> Sincronizar Financeiro com Turmas
        </Button>
      )}
    </div>
  );
};

export const ReplicateFinancialDialog = ({ year, overview, open, onOpenChange, presetCourseId, onDone }) => {
  const courses = useMemo(() => overview?.courses || [], [overview]);
  const [sourceId, setSourceId] = useState('');
  const [mode, setMode] = useState('classes');
  const [planIds, setPlanIds] = useState([]);
  const [conflict, setConflict] = useState('keep');
  const [manualCourses, setManualCourses] = useState([]);
  const [manualClasses, setManualClasses] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const source = courses.find((c) => c.id === sourceId);

  useEffect(() => {
    if (!open) return;
    const initial = presetCourseId || courses.find((c) => (c.plans_count || 0) > 0)?.id || courses[0]?.id || '';
    setSourceId(initial);
    setMode('classes');
    setConflict('keep');
    setManualCourses([]);
    setManualClasses([]);
    setPreview(null);
  }, [open, presetCourseId, courses]);

  useEffect(() => {
    const c = courses.find((x) => x.id === sourceId);
    setPlanIds((c?.plans || []).map((p) => p.id));
    setPreview(null);
  }, [sourceId, courses]);

  const body = useCallback(() => ({
    year, source_course_id: sourceId, target_mode: mode, plan_ids: planIds,
    target_course_ids: manualCourses, target_class_ids: manualClasses, conflict_mode: conflict,
  }), [year, sourceId, mode, planIds, manualCourses, manualClasses, conflict]);

  const doPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/financial/replication/preview', body());
      setPreview(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const doApply = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/financial/replication/apply', body());
      const s = data.summary || {};
      toast.success(`Replicado: ${s.new} novos · ${s.updated} atualizados · ${s.kept} mantidos · nenhum título gerado.`);
      onDone?.();
      onOpenChange(false);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const sum = preview?.summary || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="dialog-replicate-financial">
        <DialogHeader>
          <DialogTitle>Replicar planos financeiros · {year}</DialogTitle>
          <DialogDescription>
            Copia os planos-base configurados por você para turmas e cursos. Nenhum título é gerado aqui.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase">Origem · curso</Label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger data-testid="replicate-source-course"><SelectValue placeholder="Curso de origem" /></SelectTrigger>
                <SelectContent>
                  {courses.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} · {c.segment} · {c.plans_count} plano(s)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {source && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Segmento: <b>{source.segment}</b> · {source.classes_total} turma(s) no ano
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs font-bold uppercase">Destino</Label>
              <div className="space-y-1 mt-1">
                {TARGET_MODES.map(([v, label]) => (
                  <label key={v} className="flex items-center gap-2 text-sm">
                    <input type="radio" checked={mode === v} onChange={() => { setMode(v); setPreview(null); }}
                      data-testid={`replicate-mode-${v}`} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {mode === 'manual' && (
              <div className="rounded-md border p-2 max-h-48 overflow-y-auto space-y-2" data-testid="replicate-manual-picker">
                <p className="text-[11px] font-bold uppercase text-muted-foreground">Cursos destino</p>
                {courses.filter((c) => c.id !== sourceId).map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-xs">
                    <Checkbox checked={manualCourses.includes(c.id)}
                      onCheckedChange={(v) => setManualCourses((p) => v ? [...p, c.id] : p.filter((x) => x !== c.id))} />
                    {c.name}
                  </label>
                ))}
                <p className="text-[11px] font-bold uppercase text-muted-foreground pt-1">Turmas do curso de origem</p>
                {(source?.classes || []).map((cg) => (
                  <label key={cg.class_group_id} className="flex items-center gap-2 text-xs">
                    <Checkbox checked={manualClasses.includes(cg.class_group_id)}
                      onCheckedChange={(v) => setManualClasses((p) => v ? [...p, cg.class_group_id] : p.filter((x) => x !== cg.class_group_id))} />
                    {cg.name}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase">Planos a replicar</Label>
              <div className="rounded-md border p-2 mt-1 space-y-1 max-h-44 overflow-y-auto" data-testid="replicate-plan-list">
                {(source?.plans || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">Este curso não tem plano-base em {year}.</p>
                )}
                {(source?.plans || []).map((p) => (
                  <label key={p.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex items-center gap-2">
                      <Checkbox checked={planIds.includes(p.id)}
                        data-testid={`replicate-plan-${p.category}`}
                        onCheckedChange={(v) => setPlanIds((prev) => v ? [...prev, p.id] : prev.filter((x) => x !== p.id))} />
                      {p.label || p.category}
                    </span>
                    <span className="text-muted-foreground">
                      {p.num_installments}x {money(p.installment_value)} · dia {p.due_day}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase">Se o destino já tiver plano</Label>
              <div className="space-y-1 mt-1">
                {CONFLICT_MODES.map(([v, label]) => (
                  <label key={v} className="flex items-center gap-2 text-sm">
                    <input type="radio" checked={conflict === v} onChange={() => { setConflict(v); setPreview(null); }}
                      data-testid={`replicate-conflict-${v}`} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {preview && (
          <div className="space-y-3 rounded-lg border p-3 bg-slate-50" data-testid="replicate-preview">
            <p className="text-xs font-bold uppercase tracking-wide">Previsão</p>
            <p className="text-sm">
              Origem: <b>{preview.source.segment} / {preview.source.course_name} / {preview.year}</b> ·
              Destino: <b>{preview.totals.courses} curso(s)</b>, <b>{preview.totals.classes} turma(s)</b> ·
              Planos: <b>{preview.totals.plans}</b> · Ações previstas: <b>{preview.totals.actions}</b>
            </p>
            <div className="grid grid-cols-4 gap-2">
              <StatBox label="Novos" value={sum.new} tone="emerald" />
              <StatBox label="Já iguais" value={sum.equal} tone="slate" />
              <StatBox label="Divergentes" value={sum.diverged} tone="amber" />
              <StatBox label={conflict === 'keep' ? 'Mantidos' : 'Atualizados'} value={conflict === 'keep' ? sum.kept : sum.updated} tone="blue" />
            </div>
            {(preview.missing_start_date || []).length > 0 && (
              <p className="text-[11px] text-red-700 flex items-start gap-1" data-testid="replicate-missing-dates">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                {preview.missing_start_date.length} plano(s) de origem sem data inicial
                ({preview.missing_start_date.map((p) => p.label).join(', ')}): o destino vai usar
                1º de janeiro do ano letivo. Configure a data no plano do curso.
              </p>
            )}

            <div className="rounded border bg-white overflow-x-auto" data-testid="replicate-dates-preview">
              <p className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-wide bg-muted">
                Datas · origem → destino ({year})
              </p>
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50"><tr>
                  <th className="text-left p-2">Plano</th>
                  <th className="text-left p-2">Origem (data inicial · 1ª → última)</th>
                  <th className="text-left p-2">Destino (data inicial · 1ª → última)</th>
                  <th className="text-left p-2">Hoje no destino</th>
                </tr></thead>
                <tbody>
                  {(preview.source_dates || []).map((r) => (
                    <tr key={r.plan_id} className="border-t" data-testid={`replicate-date-row-${r.category}`}>
                      <td className="p-2">
                        <div className="font-semibold">{r.label}</div>
                        <div className="text-muted-foreground">{r.plan_name} · {r.source_year || '—'}</div>
                      </td>
                      <td className="p-2 text-slate-600">
                        {brDate(r.origin?.start_date)} · {brDate(r.origin?.first)} → {brDate(r.origin?.last)}
                      </td>
                      <td className="p-2 font-semibold text-emerald-700">
                        {brDate(r.destination?.start_date)} · {brDate(r.destination?.first)} → {brDate(r.destination?.last)}
                        <span className="ml-1 font-normal text-muted-foreground">
                          ({r.destination?.installments || 0}x)
                        </span>
                      </td>
                      <td className="p-2">—</td>
                    </tr>
                  ))}
                  {(preview.course_rows || []).filter((r) => r.dates).map((r, i) => (
                    <tr key={`${r.course_id}-${r.category}-${i}`}
                      className={`border-t ${r.dates.changes_date ? 'bg-amber-50' : ''}`}>
                      <td className="p-2">
                        <div className="font-semibold">{r.label}</div>
                        <div className="text-muted-foreground">{r.course_name}</div>
                      </td>
                      <td className="p-2 text-slate-600">
                        {brDate(r.dates.origin?.start_date)} · {brDate(r.dates.origin?.first)} → {brDate(r.dates.origin?.last)}
                      </td>
                      <td className="p-2 font-semibold text-emerald-700">
                        {brDate(r.dates.destination?.start_date)} · {brDate(r.dates.destination?.first)} → {brDate(r.dates.destination?.last)}
                      </td>
                      <td className={`p-2 ${r.dates.changes_date ? 'text-amber-800 font-semibold' : 'text-muted-foreground'}`}>
                        {r.dates.current
                          ? `${brDate(r.dates.current.start_date)}${r.dates.changes_date ? ' · vai mudar' : ' · igual'}`
                          : 'sem plano ainda'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(preview.class_rows || []).length > 0 && (
              <div className="max-h-52 overflow-y-auto rounded border bg-white">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted sticky top-0"><tr>
                    <th className="text-left p-2">Turma</th><th className="text-left p-2">Curso</th><th className="text-left p-2">Alterações</th>
                  </tr></thead>
                  <tbody>
                    {preview.class_rows.map((r) => (
                      <tr key={r.class_group_id} className="border-t">
                        <td className="p-2 font-semibold">{r.class_name}</td>
                        <td className="p-2 text-muted-foreground">{r.course_name}</td>
                        <td className="p-2">
                          {(r.changes || []).map((c, i) => (
                            <Badge key={i} variant="outline" className={`mr-1 mb-1 ${
                              c.action === 'new' ? 'bg-emerald-50 text-emerald-700'
                                : c.action === 'equal' ? 'bg-slate-50 text-slate-600'
                                : c.action === 'kept' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                              {c.label}: {c.action === 'new' ? `novo ${money(c.origin_value)}`
                                : c.action === 'equal' ? 'igual'
                                : `${money(c.destination_value)} → ${money(c.origin_value)} (${c.action === 'kept' ? 'mantido' : 'atualizar'})`}
                              {c.origin_date && (
                                <span className="ml-1 font-normal">
                                  · 1º venc. {c.destination_date && c.destination_date !== c.origin_date
                                    ? `${brDate(c.destination_date)} → ${brDate(c.origin_date)}`
                                    : brDate(c.origin_date)}
                                </span>
                              )}
                            </Badge>
                          ))}
                          {(r.changes || []).length === 0 && <span className="text-muted-foreground">sem alteração</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(preview.classes_without_course || []).length > 0 && (
              <p className="text-[11px] text-amber-700 flex items-start gap-1">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                {preview.classes_without_course.length} turma(s) sem curso vinculado foram ignoradas:
                {' '}{preview.classes_without_course.slice(0, 4).map((c) => c.name).join(', ')}
              </p>
            )}
            <p className="text-[11px] text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Nenhum título/boleto é gerado por esta ação.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          {!preview ? (
            <Button onClick={doPreview} disabled={loading || !sourceId || planIds.length === 0}
              data-testid="btn-replicate-preview">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Visualizar prévia
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setPreview(null)}>Voltar</Button>
              <Button onClick={doApply} disabled={loading} className="bg-violet-600 hover:bg-violet-700"
                data-testid="btn-replicate-apply">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Replicar Financeiro
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SegmentBulkValuesButton = ({ year, overview, onDone }) => {
  const [open, setOpen] = useState(false);
  const [segment, setSegment] = useState('');
  const [courseIds, setCourseIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [createMissing, setCreateMissing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const courses = useMemo(() => overview?.courses || [], [overview]);
  const segments = useMemo(() => [...new Set(courses.map((c) => c.segment))], [courses]);
  const segCourses = courses.filter((c) => c.segment === segment);

  useEffect(() => {
    if (!open) return;
    const s = segments[0] || '';
    setSegment(s);
    setPreview(null);
    setCreateMissing(false);
  }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const list = courses.filter((c) => c.segment === segment);
    setCourseIds(list.map((c) => c.id));
    const cats = {};
    list.forEach((c) => (c.plans || []).forEach((p) => {
      if (!cats[p.category]) cats[p.category] = { category: p.category, label: p.label || p.category,
        installment_value: p.installment_value, num_installments: p.num_installments, due_day: p.due_day };
    }));
    setRows(Object.values(cats));
    setPreview(null);
  }, [segment, courses]);

  const call = async (dry) => {
    setLoading(true);
    try {
      const { data } = await api.post('/financial/replication/segment/bulk-values', {
        year, segment, course_ids: courseIds, create_missing: createMissing, dry_run: dry,
        values: rows.map((r) => ({
          category: r.category,
          installment_value: r.installment_value === '' ? null : Number(r.installment_value),
          num_installments: r.num_installments === '' ? null : Number(r.num_installments),
          due_day: r.due_day === '' ? null : Number(r.due_day),
        })),
      });
      if (dry) setPreview(data);
      else {
        toast.success(`Valores aplicados: ${data.summary.updated} atualizados · ${data.summary.created} criados · ${data.summary.equal} já iguais.`);
        onDone?.();
        setOpen(false);
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const setRow = (cat, key, value) => setRows((p) => p.map((r) => r.category === cat ? { ...r, [key]: value } : r));

  return (
    <>
      <Button variant="outline" className="border-sky-300 text-sky-700" onClick={() => setOpen(true)}
        data-testid="btn-segment-bulk-values">
        <SlidersHorizontal className="w-4 h-4 mr-1" /> Editar valores do segmento
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="dialog-segment-bulk-values">
          <DialogHeader>
            <DialogTitle>Editar valores do segmento · {year}</DialogTitle>
            <DialogDescription>Altere a tabela-base e aplique aos cursos selecionados. Nenhum título é gerado.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label className="text-xs font-bold uppercase">Segmento</Label>
              <Select value={segment} onValueChange={setSegment}>
                <SelectTrigger data-testid="bulk-segment-select"><SelectValue placeholder="Segmento" /></SelectTrigger>
                <SelectContent>
                  {segments.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-md border p-2 max-h-32 overflow-y-auto">
              <p className="text-[11px] font-bold uppercase text-muted-foreground">Cursos</p>
              {segCourses.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-xs">
                  <Checkbox checked={courseIds.includes(c.id)}
                    onCheckedChange={(v) => setCourseIds((p) => v ? [...p, c.id] : p.filter((x) => x !== c.id))} />
                  {c.name}
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-md border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted"><tr>
                <th className="text-left p-2">Categoria</th><th className="p-2">Valor</th>
                <th className="p-2">Parcelas</th><th className="p-2">Dia venc.</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={4} className="p-3 text-center text-muted-foreground text-xs">
                    Nenhum plano-base neste segmento/ano.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.category} className="border-t">
                    <td className="p-2 font-semibold">{r.label}</td>
                    <td className="p-2"><Input type="number" step="0.01" className="h-8" value={r.installment_value ?? ''}
                      data-testid={`bulk-value-${r.category}`}
                      onChange={(e) => setRow(r.category, 'installment_value', e.target.value)} /></td>
                    <td className="p-2"><Input type="number" className="h-8 w-20" value={r.num_installments ?? ''}
                      onChange={(e) => setRow(r.category, 'num_installments', e.target.value)} /></td>
                    <td className="p-2"><Input type="number" className="h-8 w-20" value={r.due_day ?? ''}
                      onChange={(e) => setRow(r.category, 'due_day', e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={createMissing} onCheckedChange={(v) => setCreateMissing(!!v)} />
            Criar o plano nos cursos que ainda não têm essa categoria
          </label>

          {preview && (
            <div className="rounded-lg border p-3 bg-slate-50 space-y-2" data-testid="bulk-values-preview">
              <p className="text-xs font-bold uppercase">Previsão</p>
              <div className="grid grid-cols-4 gap-2">
                <StatBox label="Atualizar" value={preview.summary.updated} tone="blue" />
                <StatBox label="Já iguais" value={preview.summary.equal} tone="slate" />
                <StatBox label="Criar" value={preview.summary.created} tone="emerald" />
                <StatBox label="Sem plano" value={preview.summary.missing} tone="amber" />
              </div>
              <div className="max-h-44 overflow-y-auto rounded border bg-white">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted sticky top-0"><tr>
                    <th className="text-left p-2">Curso</th><th className="text-left p-2">Categoria</th>
                    <th className="text-left p-2">De → Para</th><th className="p-2">Ação</th>
                  </tr></thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2">{r.course_name}</td>
                        <td className="p-2">{r.label}</td>
                        <td className="p-2">
                          {r.current ? `${money(r.current.installment_value)} → ${money(r.new.installment_value)}` : money(r.new.installment_value)}
                        </td>
                        <td className="p-2 text-center">{r.action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            {!preview ? (
              <Button onClick={() => call(true)} disabled={loading || rows.length === 0} data-testid="btn-bulk-preview">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Visualizar prévia
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setPreview(null)}>Voltar</Button>
                <Button onClick={() => call(false)} disabled={loading} className="bg-sky-600 hover:bg-sky-700"
                  data-testid="btn-bulk-apply">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Aplicar aos cursos selecionados
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ReplicateFinancialButton = ({ year, overview, onDone }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setOpen(true)}
        data-testid="btn-replicate-financial">
        <Zap className="w-4 h-4 mr-1" /> Replicar Financeiro
      </Button>
      <ReplicateFinancialDialog year={year} overview={overview} open={open} onOpenChange={setOpen} onDone={onDone} />
    </>
  );
};
